import {
  doAdminQueries,
  doPaginationQuery,
  IQueryInfo,
} from '../providers/mutations'
import { List } from 'infrastructure/api'
import type { ResourceQuery } from 'infrastructure/api'
import {
  DemoEnvironmentResource,
  DistrictResource,
  DistrictSchoolResource,
  SchoolResource,
  UserResource,
} from '../providers/resources'
import { sharedQueryFields } from './utility'
import { fetchUserCSV } from './user.atoms'

export interface ListQueryParams {
  perPage: number
  currentPage: number
  filters: Record<string, string | number | boolean>
  sort: { field: string; order: 'asc' | 'desc' }
}

const paginatedResourceMap = {
  districts: {
    fields: DistrictResource.listQuery.fields,
  },
  districtSchools: {
    fields: DistrictSchoolResource.listQuery.fields,
  },
  users: {
    fields: UserResource.listQuery.fields,
  },
  schools: {
    fields: SchoolResource.listQuery.fields,
  },
  demos: {
    fields: DemoEnvironmentResource.listQuery.fields,
  },
}

export function fetchResourceList<T>(
  resourceId: keyof typeof paginatedResourceMap,
  queryParams: ListQueryParams,
  totalCount = 500,
) {
  const { perPage, currentPage, filters, sort } = queryParams
  const per_page = perPage < 0 ? totalCount : perPage
  return doPaginationQuery<T>(
    resourceId,
    paginatedResourceMap[resourceId].fields,
    { per_page, current_page: currentPage },
    sort,
    filters,
  )
}

export async function fetchSchools(params: ListQueryParams) {
  return fetchResourceList<List.School>('schools', params)
}

const unPaginatedResourceMap: Record<string, ResourceQuery> = {
  partners: {
    key: 'allPartners',
    fields: sharedQueryFields.partner,
  },
  products: {
    key: 'allProducts',
    fields: sharedQueryFields.product,
  },
  productGroups: {
    key: 'allGroups',
    fields: sharedQueryFields.productGroups,
  },
  primaryTrees: {
    key: 'allPrimaryTrees',
    fields: sharedQueryFields.primaryTrees,
  },
  editableTrees: {
    key: 'allEditableTrees',
    fields: sharedQueryFields.editableTrees,
  },
  crosswalkMappingSets: {
    key: 'crosswalkMappingSets',
    fields: sharedQueryFields.crosswalkMappingSets,
  },
  frameworks: {
    key: 'frameworks',
    fields: sharedQueryFields.frameworks,
  },
  standardAlignments: {
    key: 'standardAlignments',
    fields: sharedQueryFields.standardAlignments,
  },
}

export async function fetchUnPaginatedResourceList<
  T extends { id: number | string },
>(resourceId: string) {
  if (!(resourceId in unPaginatedResourceMap))
    return { result: [], error: `Resource ID not found: ${resourceId}` }
  const queryData = unPaginatedResourceMap[resourceId]
  const query: IQueryInfo = {
    resourceData: queryData,
    params: {},
  }
  const result = await doAdminQueries<T[]>([query])
  const error = result?.errors?.map(({ message }) => message).join('\n')
  return {
    result: error ? [] : (result.data?.[queryData.key] ?? []),
    errors: result.errors,
    error,
  }
}

export async function fetchDistrictSchools(params: ListQueryParams) {
  return fetchResourceList<List.DistrictSchool>('districtSchools', params)
}

export const csvMap: Record<string, Function> = {
  users: fetchUserCSV,
}
